import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'

export default function Agencies() {
  const navigate = useNavigate()
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [agencies, setAgencies] = useState([])
  const [searchFilter, setSearchFilter] = useState('')
  const [showAddModal, setShowAddModal] = useState(false)
  const [newAgency, setNewAgency] = useState({ name: '', description: '', website: '', industry: '' })

  useEffect(() => {
    checkAuth()
  }, [])

  async function checkAuth() {
    try {
      const { data: { session } } = await supabase.auth.getSession()

      if (!session) {
        navigate('/login', { replace: true })
        return
      }

      setUser(session.user)
      await loadUserProfile(session.user.id)
      await loadAgencies()
      setLoading(false)
    } catch (error) {
      console.error('Auth error:', error)
      navigate('/login', { replace: true })
    }
  }

  async function loadUserProfile(userId) {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle()

    if (error) {
      console.error('Error loading profile:', error)
    } else if (data) {
      setUserProfile(data)

      if (!data.is_premium && data.role !== 'admin') {
        navigate('/upgrade', { replace: true })
        return
      }
    }
  }

  async function loadAgencies() {
    const { data, error } = await supabase
      .from('agencies')
      .select('*')
      .order('name')

    if (error) {
      console.error('Error loading agencies:', error)
    } else {
      setAgencies(data || [])
    }
  }

  async function handleAddAgency() {
    if (!newAgency.name.trim()) {
      alert('Agency name is required')
      return
    }

    const { error } = await supabase
      .from('agencies')
      .insert({
        name: newAgency.name,
        description: newAgency.description,
        website: newAgency.website,
        industry: newAgency.industry,
        common_patterns: []
      })

    if (error) {
      console.error('Error adding agency:', error)
      alert('Error adding agency. It may already exist.')
    } else {
      await loadAgencies()
      setShowAddModal(false)
      setNewAgency({ name: '', description: '', website: '', industry: '' })
    }
  }

  async function handleSignOut() {
    await supabase.auth.signOut()
    navigate('/')
  }

  const isAdmin = userProfile?.role === 'admin'
  const isPremiumOrAdmin = userProfile?.is_premium || userProfile?.role === 'admin'

  const filteredAgencies = agencies.filter(agency =>
    agency.name.toLowerCase().includes(searchFilter.toLowerCase()) ||
    agency.description?.toLowerCase().includes(searchFilter.toLowerCase()) ||
    agency.industry?.toLowerCase().includes(searchFilter.toLowerCase())
  )

  if (loading) {
    return <div className="loading"><div className="spinner"></div></div>
  }

  return (
    <div style={{ minHeight: '100vh' }}>
      <div className="nav">
        <div className="nav-content">
          <div
            className="nav-title"
            onClick={() => navigate('/')}
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px' }}
          >
            <span>&gt; AgencySniper</span>
          </div>
          <div className="nav-actions">
            <button onClick={() => navigate('/dashboard')} className="btn-secondary" style={{ padding: '10px 20px', fontSize: '14px' }}>
              Dashboard
            </button>
            <button onClick={() => navigate('/saved')} className="btn-secondary" style={{ padding: '10px 20px', fontSize: '14px' }}>
              Saved
            </button>
            <button onClick={() => navigate('/recent-searches')} className="btn-secondary" style={{ padding: '10px 20px', fontSize: '14px' }}>
              Recent
            </button>
            {isAdmin && (
              <button onClick={() => navigate('/admin')} className="btn-secondary" style={{ padding: '10px 20px', fontSize: '14px' }}>
                Admin
              </button>
            )}
            <button onClick={handleSignOut} className="btn-secondary" style={{ padding: '10px 20px', fontSize: '14px' }}>
              Sign Out
            </button>
          </div>
        </div>
      </div>

      <div className="container">
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h2 style={{ fontSize: '32px', fontWeight: '700', color: '#90EE90', margin: 0 }}>
              &gt; Agency Database
            </h2>
            {isAdmin && (
              <button
                onClick={() => setShowAddModal(true)}
                className="btn"
                style={{ padding: '10px 20px', fontSize: '14px' }}
              >
                + Add Agency
              </button>
            )}
          </div>

          <p style={{ color: 'rgba(144, 238, 144, 0.8)', marginBottom: '28px', fontSize: '15px' }}>
            Auto-detected and manually added agencies - Automatically filtered from target results
          </p>

          <input
            type="text"
            className="input"
            placeholder="Search agencies..."
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            style={{ marginBottom: '24px' }}
          />

          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>Agency Name</th>
                  <th>Industry</th>
                  <th>Description</th>
                  <th>Website</th>
                </tr>
              </thead>
              <tbody>
                {filteredAgencies.map((agency) => (
                  <tr key={agency.id}>
                    <td style={{ fontWeight: '700', color: '#00FF00' }}>{agency.name}</td>
                    <td style={{ color: 'rgba(0, 255, 0, 0.8)' }}>{agency.industry || 'General'}</td>
                    <td style={{ color: 'rgba(0, 255, 0, 0.7)' }}>{agency.description || 'N/A'}</td>
                    <td>
                      {agency.website ? (
                        <a
                          href={agency.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#00FF00', textDecoration: 'underline' }}
                        >
                          Visit →
                        </a>
                      ) : (
                        <span style={{ color: 'rgba(0, 255, 0, 0.4)' }}>N/A</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredAgencies.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: 'rgba(0, 255, 0, 0.6)' }}>
              <p>No agencies found matching your search</p>
            </div>
          )}
        </div>
      </div>

      {showAddModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div className="card" style={{ maxWidth: '600px', width: '100%', margin: 0 }}>
            <h3 style={{ fontSize: '24px', marginBottom: '20px', color: '#00FF00' }}>
              &gt; Add New Agency
            </h3>

            <div style={{ marginBottom: '16px' }}>
              <label className="label">Agency Name *</label>
              <input
                type="text"
                className="input"
                value={newAgency.name}
                onChange={(e) => setNewAgency({ ...newAgency, name: e.target.value })}
                placeholder="e.g., Robert Half"
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label className="label">Industry</label>
              <input
                type="text"
                className="input"
                value={newAgency.industry}
                onChange={(e) => setNewAgency({ ...newAgency, industry: e.target.value })}
                placeholder="e.g., Finance, Technology, Healthcare"
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label className="label">Description</label>
              <textarea
                className="input"
                value={newAgency.description}
                onChange={(e) => setNewAgency({ ...newAgency, description: e.target.value })}
                placeholder="Brief description of the agency..."
                style={{ minHeight: '80px' }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label className="label">Website</label>
              <input
                type="text"
                className="input"
                value={newAgency.website}
                onChange={(e) => setNewAgency({ ...newAgency, website: e.target.value })}
                placeholder="https://example.com"
              />
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => {
                  setShowAddModal(false)
                  setNewAgency({ name: '', description: '', website: '', industry: '' })
                }}
                className="btn-secondary"
                style={{ flex: 1 }}
              >
                Cancel
              </button>
              <button
                onClick={handleAddAgency}
                className="btn"
                style={{ flex: 1 }}
              >
                Add Agency
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
